import React from "react";
import useSWR from "swr";

interface ConfigContext {
  stripeKey: string;
  mapboxApiAccessToken: string;
  sentryDSN: string;
  environment: "development" | "staging" | "production";
  auth0Domain: string;
  auth0ClientId: string;
  auth0ApiAudience: string;
  auth0PortalDomain: string;
  auth0PortalClientId: string;
  auth0PortalApiAudience: string;
  splitioApiKey: string;
  tranzilaNotifyURLAddress: string;
  // Origin of the Eve agent API (scheme, host and port; no path and no trailing
  // slash). "" means Eve is not set up for this environment, so anything that
  // talks to it stays off.
  eveApiUrl: string;
}

const initialConfig: ConfigContext = {
  stripeKey: "",
  mapboxApiAccessToken: "",
  sentryDSN: "",
  environment: "development",
  auth0Domain: "",
  auth0ClientId: "",
  auth0ApiAudience: "",
  auth0PortalDomain: "",
  auth0PortalClientId: "",
  auth0PortalApiAudience: "",
  splitioApiKey: "",
  tranzilaNotifyURLAddress: "",
  eveApiUrl: "",
};

const ConfigContext = React.createContext<ConfigContext>(initialConfig);

export function useConfig() {
  return React.useContext(ConfigContext);
}

interface ConfigProviderProps {
  children?: React.ReactNode;
}

// We fetch config to be able to get staging vs production environment
export function ConfigProvider(props: ConfigProviderProps) {
  const { children } = props;
  const data = useConfigQuery();

  return (
    <ConfigContext.Provider
      value={{
        stripeKey: data?.stripeKey ?? "",
        mapboxApiAccessToken: data?.mapboxApiAccessToken ?? "",
        sentryDSN: data?.sentryDSN ?? "",
        environment: data?.environment ?? "",
        auth0Domain: data?.auth0Domain ?? "",
        auth0ClientId: data?.auth0ClientId ?? "",
        auth0ApiAudience: data?.auth0ApiAudience ?? "",
        auth0PortalDomain: data?.auth0PortalDomain ?? "",
        auth0PortalClientId: data?.auth0PortalClientId ?? "",
        auth0PortalApiAudience: data?.auth0PortalApiAudience ?? "",
        splitioApiKey: data?.splitioApiKey ?? "",
        tranzilaNotifyURLAddress: data?.tranzilaNotifyURLAddress ?? "",
        eveApiUrl: data?.eveApiUrl ?? "",
      }}
    >
      {children}
    </ConfigContext.Provider>
  );
}

export function useConfigQuery() {
  const response = useSWR(
    "/web_ui_config",
    async (input) => {
      const res = await fetch(input);

      const body = await res.json();

      if (res.status >= 200 && res.status < 300) {
        return body;
      }

      throw new Error(body.message);
    },
    { suspense: true }
  );

  return {
    stripeKey: response.data.REACT_APP_STRIPE_PUBLISHABLE_KEY,
    mapboxApiAccessToken: response.data.REACT_APP_MAPBOX_API_ACCESS_TOKEN,
    sentryDSN: response.data.REACT_APP_SENTRY_DSN,
    environment: response.data.REACT_APP_ENVIRONMENT,
    auth0Domain: response.data.REACT_APP_AUTH0_DOMAIN,
    auth0ClientId: response.data.REACT_APP_AUTH0_CLIENT_ID,
    auth0ApiAudience: response.data.REACT_APP_AUTH0_API_AUDIENCE,
    auth0PortalDomain: response.data.REACT_APP_AUTH0_PORTAL_DOMAIN,
    auth0PortalClientId: response.data.REACT_APP_AUTH0_PORTAL_CLIENT_ID,
    auth0PortalApiAudience: response.data.REACT_APP_AUTH0_PORTAL_API_AUDIENCE,
    splitioApiKey: response.data.REACT_APP_SPLITIO_BROWSER_API_KEY,
    tranzilaNotifyURLAddress:
      response.data.REACT_APP_TRANZILA_NOTIFY_URL_ADDRESS,
    eveApiUrl: toEveApiUrl(response.data.REACT_APP_EVE_API_URL),
  };
}

// The panel calls Eve over fetch, which no other scheme supports.
const EVE_API_PROTOCOLS = new Set(["https:", "http:"]);

/**
 * The Eve API origin from `/web_ui_config`, where an environment without Eve
 * sends null or leaves the value blank. The value is Eve's site URL as
 * configured, and Eve serves its API from the site's root, so a path, query or
 * credentials on it are dropped instead of being prefixed onto every call. A
 * value that isn't an absolute http(s) URL would resolve against the
 * dashboard's own origin, so it turns Eve off rather than being guessed at.
 */
export function toEveApiUrl(value: unknown): string {
  if (typeof value !== "string") {
    return "";
  }

  const url = parseAbsoluteUrl(value.trim());
  if (!url || !EVE_API_PROTOCOLS.has(url.protocol)) {
    return "";
  }

  return url.origin;
}

function parseAbsoluteUrl(value: string): URL | null {
  try {
    return new URL(value);
  } catch {
    return null;
  }
}
